<template>
    <div class="relative w-full" @click="handleDropdownClick">
        <label v-if="label">
            {{ label }}
        </label>
        <!-- Floating Label -->
        <label
            v-if="floatingLabel"
            :class="[
                'absolute text-xs transition-all duration-200 z-10',
                {
                    // Warna terpisah dari posisi: error hanya mewarnai, tidak menggeser label.
                    'text-red-600': error,
                    'text-blue-500':
                        !error &&
                        displayedLabel !== placeholder &&
                        displayedLabel !== 'Not Found',
                    'text-gray-500':
                        !error &&
                        (displayedLabel === placeholder ||
                            displayedLabel === 'Not Found'),
                    'top-2 left-3':
                        displayedLabel === placeholder ||
                        displayedLabel === 'Not Found',
                },
                iconFront ? 'left-[42px]' : 'left-3',
                displayedLabel ? 'top-2' : '',
            ]"
        >
            {{ floatingLabel }}
            <span v-if="required" class="text-red-500">*</span>
        </label>

        <!-- Select Input Wrapper -->
        <div
            data-testid="select-trigger"
            :data-open="isOpen ? 'true' : 'false'"
            @click="toggleDropdown"
            :class="[
                cssClass,
                {
                    'pt-6': floatingLabel, // Tambahkan padding atas jika ada floatingLabel
                    'border-red-400 bg-red-50': error,
                    'border-blue-500':
                        !error &&
                        displayedLabel !== placeholder &&
                        displayedLabel !== 'Not Found',
                    'border-gray-500':
                        !error &&
                        (displayedLabel === placeholder ||
                            displayedLabel === 'Not Found'),
                    'bg-gray-100': disabled,
                },
            ]"
        >
            <!-- Ikon di depan -->
            <span v-if="iconFront" class="mr-2">
                <AppIcon
                    :icon="iconFront"
                    class="text-gray-600"
                    :class="['relative', floatingLabel ? 'bottom-2' : '']"
                    width="20"
                    height="20"
                />
            </span>

            <!-- Selected Option atau Placeholder -->
            <div class="flex flex-col flex-grow">
                <span :class="displayedLabelClass">
                    {{ displayedLabel }}
                </span>
            </div>

            <!-- Ikon dropdown (chevron) -->
            <span class="ml-auto">
                <AppIcon
                    icon="mdi:chevron-down"
                    class="w-4 h-4 text-gray-600"
                    :class="['relative', floatingLabel ? 'bottom-2' : '']"
                />
            </span>
        </div>

        <!-- Dropdown list -->
        <!-- <ul v-if="isOpen" class="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg select-dropdown">
            <li v-for="option in options" :key="option.value" @click="selectOption(option)"
                class="p-2 cursor-pointer hover:bg-gray-100">
                {{ option.label }}
            </li>
            <li v-if="options.length === 0" class="p-2 text-gray-500 cursor-default">Not Found</li>
        </ul> -->
        <div
            v-if="isOpen"
            data-testid="select-panel"
            class="absolute w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg select-dropdown z-[9999]"
        >
            <!-- Search Field -->
            <input
                data-testid="select-search"
                type="text"
                v-model="searchQuery"
                placeholder="Search..."
                class="w-full p-2 text-sm border-gray-300 focus:outline-none focus:ring-2 focus:ring-transparent z-[9999]"
            />

            <!-- Options -->
            <ul class="max-h-48 z-[9999]">
                <li
                    v-for="option in filteredOptions"
                    :key="option.value"
                    data-testid="select-option"
                    :data-value="option.value"
                    @click="selectOption(option)"
                    class="p-2 cursor-pointer hover:bg-gray-100"
                >
                    {{ option.label }}
                </li>
                <li
                    v-if="filteredOptions.length === 0"
                    class="p-2 text-gray-500 cursor-default"
                >
                    Not Found
                </li>
            </ul>
        </div>

        <p v-if="error" class="mt-1 text-xs text-red-600">{{ error }}</p>
    </div>
</template>

<script>
export default {
    name: "BaseSelectIcon",
    props: {
        label: {
            type: String,
            default: null,
        },
        floatingLabel: {
            type: String,
            default: "",
        },
        required: {
            type: Boolean,
            default: false,
        },
        error: {
            type: String,
            default: "",
        },
        labelClass: {
            type: String,
            default: "text-sm text-blue-500 font-medium",
        },
        options: {
            type: Array,
            required: true,
            default: () => [],
        },
        placeholder: {
            type: String,
            default: "Select option",
        },
        iconFront: {
            type: String,
            default: "",
        },
        modelValue: {
            type: [String, Number, null],
            default: null,
        },
        cssClass: {
            type: String,
            default:
                "flex items-center border rounded-lg px-3 py-2 cursor-pointer",
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isOpen: false,
            displayedLabel: this.placeholder,
            searchQuery: "",
        };
    },
    computed: {
        selectedOption() {
            return this.options.find(
                (option) => option.value === this.modelValue
            );
        },
        displayedLabelClass() {
            return !this.displayedLabel ||
                this.displayedLabel === this.placeholder ||
                this.displayedLabel === "Not Found"
                ? "text-gray-500"
                : "text-gray-700";
        },
        filteredOptions() {
            if (!this.searchQuery) {
                return this.options;
            }
            return this.options.filter((option) =>
                option.label
                    .toLowerCase()
                    .includes(this.searchQuery.toLowerCase())
            );
        },
    },
    methods: {
        toggleDropdown() {
            if (!this.disabled) this.isOpen = !this.isOpen;
        },
        selectOption(option) {
            this.$emit("update:modelValue", option.value);
            this.displayedLabel = option.label;
            this.isOpen = false;
        },
        handleDropdownClick(event) {
            const target = event.target;
            if (this.isOpen && !this.$el.contains(target)) {
                this.isOpen = false;
            }
        },
    },
    watch: {
        modelValue(newValue) {
            const selectedOption = this.options.find(
                (option) => option.value === newValue
            );
            if (selectedOption) {
                this.displayedLabel = selectedOption.label;
            } else {
                this.displayedLabel = this.placeholder;
            }
        },
    },
    mounted() {
        document.addEventListener("click", this.handleDropdownClick);

        if (this.modelValue) {
            const selectedOption = this.options.find(
                (option) => option.value === this.modelValue
            );
            this.displayedLabel = selectedOption
                ? selectedOption.label
                : this.placeholder;
        }
    },
    unmounted() {
        document.removeEventListener("click", this.handleDropdownClick);
    },
};
</script>

<style scoped>
.select-dropdown {
    max-height: 200px;
    overflow-y: auto;
}

/* Sembunyikan scrollbar di mobile/small view (tetap bisa di-scroll) */
@media (max-width: 768px) {
    .select-dropdown {
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* IE/Edge */
    }
    .select-dropdown::-webkit-scrollbar {
        display: none; /* Chrome/Safari */
    }
}

.border-blue-500 {
    border-color: #3b82f6;
}

.text-blue-500 {
    color: #3b82f6;
}
</style>
